import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Maximize2, Minimize2 } from "@digital-twins/design-system/icons";
import {
  SOCIAL_SENTIMENTS,
  SocialSentiment,
  SocialTopicCountsResponse,
  SocialTopicFilters,
  SocialTopicMetric,
  SocialTopicPostItem,
  SocialTopicPostsSort,
} from "@digital-twins/shared-types";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import { percentOf, rankCells } from "./charts/socialTopicMetrics";
import { orderSocialFeedPosts, socialFeedRank } from "./socialFeedOrder";
import {
  formatSocialMeasure,
  SOCIAL_SENTIMENT_DOT_CLASS,
  SocialPostTile,
  socialBrandSwatchClass,
} from "./SocialPostTile";
import { SocialBrandMark } from "./SocialBrandMark";
import { useTopicHighlight } from "./SocialTopicHighlightContext";
import { useSocialUniverseCounts } from "./useSocialUniverse";
import { useSocialUniversePosts } from "./useSocialUniversePosts";

/** Tiles a Theme showed before "See all" (Rev 3). Kept for its importers; boards page in full pages. */
export const SOCIAL_THEME_PREVIEW_TILES = 6;

/** Brand chips a board header names at most. */
export const SOCIAL_BOARD_HEADER_BRANDS = 2;

/** Skeletons wait this long, so a fast answer never flashes them (concept §5.11). */
const SKELETON_DELAY_MS = 150;

interface SocialThemeSectionProps {
  universeToken: string;
  /** A theme id or SOCIAL_OTHER_THEME_ID. */
  themeId: string;
  title: string;
  count: number;
  /** Channel, brand and sentiment filters of the Topic view; the theme is added here. */
  filters: SocialTopicFilters;
  sort: SocialTopicPostsSort;
  refreshKey: string | number | null;
  /** Theme and brand names by id, for the tile chips. */
  themeNames: Map<string, string>;
  brandNames: Map<string, string>;
  /** Each brand's stored picture (`SocialBrand.logoPath`); absent keeps the swatches. */
  brandLogoPaths?: Map<string, string | null>;
  /** Full list with "Load more" instead of a row of tiles and "See all". */
  isExpanded?: boolean;
  onSeeAll?: (themeId: string) => void;
  onOpenPost: (post: SocialTopicPostItem) => void;
  /** The measure that orders and labels the boards. Defaults to posts. */
  metric?: SocialTopicMetric;
  /** The theme's summed views under the filters (its count cell `views`). */
  views?: number;
  /** Posts under the filters, all themes: the "% of topic" denominator. */
  topicTotal?: number;
  /** Tile quick action, gated by permissions; absent hides it. */
  onAskPost?: (post: SocialTopicPostItem) => void;
  /** Tile quick action, gated by permissions; absent hides it. */
  onHidePost?: (post: SocialTopicPostItem) => void;
  /** The Topic's name, for the hide icon's "Hide from <Topic>". */
  topicName?: string;
  /** Posts hidden here: gone from the board at once, before the refetch lands. */
  hiddenPostTokens?: ReadonlySet<string>;
  /** The board's loaded posts, in board order, each time a page lands (for `j` / `k`). */
  onPostsChange?: (themeId: string, posts: Array<SocialTopicPostItem>) => void;
  /**
   * Far from view in the boards row: no request goes out (posts, sentiment,
   * brands) and a placeholder holds the board's place until it nears view.
   */
  isDeferred?: boolean;
  /** Sizing from the boards row (width, snap). */
  className?: string;
  /** No header: the feed alone (the topic's only theme is Other). */
  isHeaderless?: boolean;
  /** The Topic's period start (D23): tiles of earlier posts read as earlier. */
  periodFrom?: string;
}

/** Net sentiment in points, -100 to 100, from sentiment counts; null when no post has one. */
export function socialNetSentiment(
  counts: SocialTopicCountsResponse | null,
): number | null {
  if (!counts) {
    return null;
  }
  const countOf = (key: SocialSentiment) =>
    counts.cells.find((cell) => cell.key === key)?.count ?? 0;
  const judged = SOCIAL_SENTIMENTS.reduce((sum, key) => sum + countOf(key), 0);
  if (judged <= 0) {
    return null;
  }
  return Math.round(((countOf("positive") - countOf("negative")) / judged) * 100);
}

/** `text` with the first `value` in it set in the foreground at 600, as the board header reads. */
function withBoldValue(text: string, value: string): ReactNode {
  const at = text.indexOf(value);
  if (at < 0) {
    return text;
  }
  return (
    <>
      {text.slice(0, at)}
      <b className="font-semibold text-foreground">{value}</b>
      {text.slice(at + value.length)}
    </>
  );
}

/** True `delayMs` after `isOn` turned on, false as soon as it turns off. */
function useDelayedFlag(isOn: boolean, delayMs: number): boolean {
  const [isShown, setIsShown] = useState(false);

  useEffect(() => {
    if (!isOn) {
      setIsShown(false);
      return undefined;
    }
    const timer = setTimeout(() => setIsShown(true), delayMs);
    return () => clearTimeout(timer);
  }, [isOn, delayMs]);

  return isShown;
}

/**
 * One theme board (concept §5.10): the name, reach first, then posts and
 * their share of the Topic, the sentiment mix with its net, and the top
 * brands; below, the theme's posts in the page's order, in an inner scroll
 * with masonry columns (5 when expanded) and paging. Every number is a count
 * under the same filters as the board's posts.
 */
export function SocialThemeSection(props: SocialThemeSectionProps) {
  const {
    universeToken,
    themeId,
    title,
    count,
    filters,
    sort,
    refreshKey,
    brandNames,
    brandLogoPaths,
    isExpanded = false,
    isHeaderless = false,
    onSeeAll,
    onOpenPost,
    metric = "posts",
    views,
    topicTotal,
    onAskPost,
    onHidePost,
    topicName,
    hiddenPostTokens,
    onPostsChange,
    isDeferred = false,
    className,
    periodFrom,
  } = props;

  const { t, i18n } = useTranslation("app");
  const boardFilters: SocialTopicFilters = { ...filters, themeId };
  const requestToken = isDeferred ? null : universeToken;
  const {
    posts: rankedPosts,
    isLoading,
    isLoadingMore,
    hasError,
    hasMore,
    loadMore,
  } = useSocialUniversePosts(requestToken, {
    filters: boardFilters,
    sort,
    refreshKey,
  });
  // Image posts lead at equal rank, and a text tile never opens the board.
  const posts = useMemo(
    () =>
      orderSocialFeedPosts(
        hiddenPostTokens && hiddenPostTokens.size > 0
          ? rankedPosts.filter((post) => !hiddenPostTokens.has(post.token))
          : rankedPosts,
        sort,
      ),
    [rankedPosts, sort, hiddenPostTokens],
  );
  const isLeadTopRanked =
    posts.length > 0 &&
    rankedPosts.length > 0 &&
    socialFeedRank(posts[0], sort) ===
      socialFeedRank(rankedPosts[0], sort);
  const { counts: sentimentCounts } = useSocialUniverseCounts(
    requestToken,
    { groupBy: "sentiment", filters: boardFilters },
    refreshKey,
  );
  const { counts: brandCounts } = useSocialUniverseCounts(
    brandNames.size > 0 ? requestToken : null,
    { groupBy: "brand", filters: boardFilters },
    refreshKey,
  );
  const highlight = useTopicHighlight();
  const isSkeletonShown = useDelayedFlag(
    isLoading && posts.length === 0,
    SKELETON_DELAY_MS,
  );
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const onPostsChangeRef = useRef(onPostsChange);
  onPostsChangeRef.current = onPostsChange;

  useEffect(() => {
    onPostsChangeRef.current?.(themeId, posts);
  }, [themeId, posts]);

  // Paging: the next page loads as the inner scroll nears the end.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMore();
        }
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, posts.length]);

  const testId = `social-theme-${themeId}`;
  const brandOrder = Array.from(brandNames.keys());
  const percent = percentOf(count, topicTotal) ?? 0;
  const net = socialNetSentiment(sentimentCounts);
  const mix = SOCIAL_SENTIMENTS.map((key) => ({
    key,
    count: sentimentCounts?.cells.find((cell) => cell.key === key)?.count ?? 0,
  })).filter((segment) => segment.count > 0);
  const topBrands = brandCounts
    ? rankCells(
        brandCounts.cells.filter((cell) => cell.count > 0),
        (key) => brandNames.get(key) ?? key,
      ).slice(0, SOCIAL_BOARD_HEADER_BRANDS)
    : [];
  const name = title;
  const reachLabel = formatSocialMeasure(views ?? 0, i18n.language);

  function openPost(postToken: string) {
    const post = posts.find((item) => item.token === postToken);
    if (post) {
      onOpenPost(post);
    }
  }

  const header = (
    <header className="flex items-start justify-between gap-3 border-b border-card-border px-4 pb-3 pt-4">
      <div className="min-w-0">
        <h3
          id={`${testId}-heading`}
          className="truncate font-display text-[24px] font-normal leading-[1.1] text-foreground"
        >
          {name}
        </h3>
        <div
          className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] tabular-nums text-muted-foreground"
          data-testid={`${testId}-stats`}
          data-metric={metric}
        >
          <span data-testid={`${testId}-reach`}>
            {withBoldValue(
              t("socialListening.topicPage.boards.reach", { value: reachLabel }),
              reachLabel,
            )}
          </span>
          <span data-testid={`${testId}-posts`}>
            {withBoldValue(
              t("socialListening.topicPage.boards.posts", { count, percent }),
              String(count),
            )}
          </span>
          {mix.length > 0 ? (
            <span
              className="flex h-1 w-20 gap-px overflow-hidden rounded-sm bg-[hsl(var(--chart-bar-track))]"
              role="img"
              aria-label={t("socialListening.topicPage.boards.sentimentMix")}
              title={t("socialListening.topicPage.boards.sentimentMix")}
              data-testid={`${testId}-mix`}
            >
              {mix.map((segment) => (
                <span
                  key={segment.key}
                  className={cn("h-full", SOCIAL_SENTIMENT_DOT_CLASS[segment.key])}
                  style={{ flexGrow: segment.count }}
                  data-sentiment={segment.key}
                />
              ))}
            </span>
          ) : null}
          {net != null ? (
            <span data-testid={`${testId}-net`}>
              {t("socialListening.topicPage.boards.net", {
                value: `${net > 0 ? "+" : ""}${net}`,
              })}
            </span>
          ) : null}
          {topBrands.map((cell) => (
            <span
              key={cell.key}
              className="inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full border border-border px-2 text-[12px] text-foreground"
              onPointerEnter={() =>
                highlight.setActive({ kind: "brand", brandId: cell.key })
              }
              onPointerLeave={() => highlight.setActive(null)}
              data-testid={`${testId}-brand-${cell.key}`}
            >
              <SocialBrandMark
                logoPath={brandLogoPaths?.get(cell.key)}
                swatch={
                  <span
                    className={cn(
                      "h-2 w-2 rounded-sm",
                      socialBrandSwatchClass(brandOrder.indexOf(cell.key)),
                    )}
                    aria-hidden="true"
                  />
                }
                testId={`${testId}-brand-mark-${cell.key}`}
              />
              {brandNames.get(cell.key) ?? cell.key} {cell.count}
            </span>
          ))}
        </div>
      </div>
      {onSeeAll ? (
        <button
          type="button"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={
            isExpanded
              ? t("socialListening.topicPage.boards.collapse", { name })
              : t("socialListening.topicPage.boards.expand", { name })
          }
          aria-expanded={isExpanded}
          title={
            isExpanded
              ? t("socialListening.topicPage.boards.collapse", { name })
              : t("socialListening.topicPage.boards.expand", { name })
          }
          data-testid={`${testId}-expand`}
          onClick={() => onSeeAll(themeId)}
        >
          {isExpanded ? (
            <Minimize2 className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      ) : null}
    </header>
  );

  const masonryClass = cn(
    "[column-gap:8px] [columns:2_140px] @[760px]:[column-gap:12px]",
    isExpanded ? "@[760px]:[columns:5_230px]" : "@[760px]:[columns:2_220px]",
  );

  const skeleton = (
    <div className={masonryClass} aria-hidden="true">
      {Array.from({ length: isExpanded ? 10 : 4 }).map((_, index) => (
        <div
          key={index}
          className={cn(
            "relative mb-3 break-inside-avoid overflow-hidden rounded-[calc(var(--radius)+4px)] border border-card-border",
            index % 2 === 0 ? "h-72" : "h-56",
          )}
        >
          <div className="absolute inset-0 animate-pulse bg-foreground/[0.04] motion-reduce:animate-none" />
        </div>
      ))}
    </div>
  );

  const body = isDeferred ? (
    skeleton
  ) : isLoading && posts.length === 0 ? (
      isSkeletonShown ? skeleton : null
    ) : hasError && posts.length === 0 ? (
      <Text size="sm" color="muted" data-testid={`${testId}-error`}>
        {t("socialListening.topicView.postsFailed")}
      </Text>
    ) : posts.length === 0 ? (
      <div
        className="grid h-full place-items-center p-6 text-center text-[14px] text-muted-foreground"
        data-testid={`${testId}-empty`}
      >
        {t("socialListening.topicPage.boards.empty")}
      </div>
    ) : (
      <>
        <div className={masonryClass} data-testid={`${testId}-posts`}>
          {posts.map((post, postIndex) => {
            const tag =
              post.brands.find((item) => !item.seen && brandNames.has(item.id)) ??
              post.brands.find((item) => brandNames.has(item.id)) ??
              null;
            return (
              <SocialPostTile
                key={post.token}
                post={post}
                periodFrom={periodFrom}
                brandName={tag ? (brandNames.get(tag.id) ?? null) : null}
                brandLogoPath={tag ? (brandLogoPaths?.get(tag.id) ?? null) : null}
                isBrandSeen={tag?.seen === true}
                brandSwatchClass={
                  tag
                    ? socialBrandSwatchClass(brandOrder.indexOf(tag.id))
                    : undefined
                }
                isTop={
                  postIndex === 0 &&
                  isLeadTopRanked &&
                  sort !== "newest" &&
                  posts.length > 1
                }
                onOpen={openPost}
                onAsk={
                  onAskPost
                    ? () => onAskPost(post)
                    : undefined
                }
                onHide={
                  onHidePost
                    ? () => onHidePost(post)
                    : undefined
                }
                hideTopicName={topicName}
              />
            );
          })}
        </div>
        {hasMore ? (
          <div ref={sentinelRef} className="pt-1">
            <Button
              type="button"
              variant="outline"
              shape="pill"
              className="w-full"
              disabled={isLoadingMore}
              data-testid={`${testId}-load-more`}
              onClick={loadMore}
            >
              {isLoadingMore
                ? t("socialListening.detail.loadingMore")
                : t("socialListening.topicPage.boards.loadMore")}
            </Button>
          </div>
        ) : null}
      </>
    );

  return (
    <section
      className={cn(
        "flex h-[clamp(560px,82vh,880px)] min-w-0 flex-col overflow-hidden rounded-[calc(var(--radius)+8px)] border border-card-border bg-background",
        className,
      )}
      aria-labelledby={isHeaderless ? undefined : `${testId}-heading`}
      aria-label={isHeaderless ? name : undefined}
      data-testid={testId}
      data-board={themeId}
      data-expanded={isExpanded ? "true" : "false"}
      data-deferred={isDeferred ? "true" : "false"}
    >
      {isHeaderless ? null : header}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3.5 [scrollbar-width:thin]">
        {body}
      </div>
    </section>
  );
}
